/**
 * The per-brand tagging categories (owner only): the categories and their values, which tags and
 * search share. Names and values rename in place; a value's tags follow it because they hold its id.
 * A value marked not current stays searchable but stops being offered; removing a category removes
 * its values and their tags. Categories reorder by their grab handle and values drag between
 * categories; both persist, and the tagging panel follows the same sequence. The categories are one
 * full-width row each, in their order (Dax, 2026-10-01): a small category takes a line, a large one wraps,
 * and the order always reads 1, 2, 3. Beside each number, up and down arrows move it one place; the
 * number renumbers live as a drag shuffles them.
 * Arriving from a Tagging cell's gear, the page scrolls to that category and its card flashes once.
 * With `media="audio"` it edits the library's sound categories instead (Studio Library only): the columns
 * every sound effect is filed along, shared by every brand (2026-10-04).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Chevron, Icon } from '@/components/icons';
import { InlineName, Pill, PillInput } from '@/components/pill-editor';
import { queryClient, trpc } from '@/lib/trpc';

type Drag = { kind: 'facet'; id: string } | { kind: 'value'; id: string; from: string };

const CATEGORY_NAME_MAX = 80;
const VALUE_NAME_MAX = 120;
/** Long enough for the cards above to take their heights, so the scroll lands where the card stays. */
const FOCUS_SCROLL_DELAY_MS = 300;

/** The approximate use count a warning quotes: asset tags and their snippet copies both count. */
function usedTimes(n: number): string {
  return n === 1 ? 'used once' : `used ${n} times`;
}

export function TaggingCategories({ focus = null, media = 'video' }: { focus?: string | null; media?: 'video' | 'audio' }) {
  const facets = useQuery(trpc.facets.list.queryOptions({ media }));
  const focusRef = useRef<HTMLDivElement>(null);
  const hasFacets = Boolean(facets.data?.length);
  useEffect(() => {
    if (!focus || !hasFacets) return;
    const t = setTimeout(() => focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), FOCUS_SCROLL_DELAY_MS);
    return () => clearTimeout(t);
  }, [focus, hasFacets]);
  const [adding, setAdding] = useState(false);
  const [drag, setDrag] = useState<Drag | null>(null);
  /** The live sequence while a category is being dragged; committed on drop. */
  const [order, setOrder] = useState<string[] | null>(null);
  const [over, setOver] = useState<string | null>(null);
  /** The category whose Remove is waiting on a second click because its values are in use. */
  const [confirmFacet, setConfirmFacet] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.facets.list.queryKey({ media }) });
  const upsertFacet = useMutation(trpc.facets.upsertFacet.mutationOptions({ onSuccess: refresh }));
  const renameFacet = useMutation(trpc.facets.renameFacet.mutationOptions({ onSuccess: refresh }));
  const deleteFacet = useMutation(trpc.facets.deleteFacet.mutationOptions({ onSuccess: refresh }));
  const upsertValue = useMutation(trpc.facets.upsertValue.mutationOptions({ onSuccess: refresh }));
  const renameValue = useMutation(trpc.facets.renameValue.mutationOptions({ onSuccess: refresh }));
  const deleteValue = useMutation(trpc.facets.deleteValue.mutationOptions({ onSuccess: refresh }));
  const reorder = useMutation(trpc.facets.reorderFacets.mutationOptions({ onSuccess: refresh }));
  const moveValue = useMutation(trpc.facets.moveValue.mutationOptions({ onSuccess: refresh }));

  const addFacet = (label: string) => {
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    upsertFacet.mutate({ key: media === 'audio' ? `sound_${key}` : key, label, order: facets.data?.length ?? 0, media });
    setAdding(false);
  };

  const ids = facets.data?.map((f) => f.id) ?? [];
  const shown = order ?? ids;
  const byId = new Map((facets.data ?? []).map((f) => [f.id, f]));
  const endDrag = () => { setDrag(null); setOrder(null); setOver(null); };
  const errors = [upsertFacet, renameFacet, upsertValue, renameValue, deleteFacet, deleteValue, reorder, moveValue].find((m) => m.isError);

  /** One place up (-1) or down (+1), by the same save a drag makes. */
  const step = (id: string, by: -1 | 1) => {
    const from = ids.indexOf(id);
    const to = from + by;
    if (from === -1 || to < 0 || to >= ids.length) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, id);
    reorder.mutate({ facetIds: next });
  };

  const cards = shown.map((id) => byId.get(id)).filter((f): f is NonNullable<typeof f> => Boolean(f)).map((f, i) => ({
    id: f.id,
    node: (
      <div ref={f.id === focus ? focusRef : undefined} className={`sect facet-row${f.id === focus ? ' facet-focus' : ''}${drag?.kind === 'facet' && drag.id === f.id ? ' dragging' : ''}${over === f.id && drag?.kind === 'value' && drag.from !== f.id ? ' drop-target' : ''}`}
        onDragOver={(e) => {
          if (!drag) return;
          e.preventDefault();
          if (drag.kind === 'value') { setOver(f.id); return; }
          // The sequence shuffles live behind the dragged category; release confirms it.
          const cur = shown.indexOf(drag.id);
          const to = shown.indexOf(f.id);
          if (cur === -1 || to === -1 || cur === to) return;
          const next = [...shown];
          next.splice(cur, 1);
          next.splice(to, 0, drag.id);
          setOrder(next);
        }}
        onDragLeave={() => { if (over === f.id) setOver(null); }}
        onDrop={(e) => {
          e.preventDefault();
          if (drag?.kind === 'value' && drag.from !== f.id) moveValue.mutate({ valueId: drag.id, facetId: f.id });
          if (drag?.kind === 'facet' && order) reorder.mutate({ facetIds: order });
          endDrag();
        }}>
        <div className="row facet-head edit-host">
          <span className="facet-grip" draggable role="button" aria-label={`Drag to reorder ${f.label}`} title="Drag to reorder"
            onDragStart={(e) => { setDrag({ kind: 'facet', id: f.id }); setOrder(ids); e.dataTransfer.effectAllowed = 'move'; }}
            onDragEnd={endDrag}>
            <Icon name="grip" />
          </span>
          <span className="row facet-order">
            <button type="button" className="facet-step" aria-label={`Move ${f.label} up`} title="Move up" disabled={i === 0 || reorder.isPending} onClick={() => step(f.id, -1)}>
              <Chevron up />
            </button>
            <span className="facet-num" title={`Position ${i + 1} on the tagging page`}>{i + 1}</span>
            <button type="button" className="facet-step" aria-label={`Move ${f.label} down`} title="Move down" disabled={i === shown.length - 1 || reorder.isPending} onClick={() => step(f.id, 1)}>
              <Chevron />
            </button>
          </span>
          <InlineName value={f.label} className="label" maxLength={CATEGORY_NAME_MAX} onRename={(label) => renameFacet.mutate({ facetId: f.id, label })} />
          {confirmFacet === f.id ? (
            <span className="row facet-confirm" role="alertdialog" aria-label={`Remove ${f.label}?`}>
              <span className="sub">Its values are {usedTimes(f.values.reduce((n, v) => n + v.uses, 0))}; their tags go too</span>
              <button type="button" className="btn quiet facet-del danger" onClick={() => { setConfirmFacet(null); deleteFacet.mutate({ facetId: f.id }); }}>Remove</button>
              <button type="button" className="btn quiet facet-del" autoFocus onClick={() => setConfirmFacet(null)}>Keep</button>
            </span>
          ) : (
            <button type="button" className="btn quiet facet-del"
              onClick={() => (f.values.some((v) => v.uses > 0) ? setConfirmFacet(f.id) : deleteFacet.mutate({ facetId: f.id }))}>Remove</button>
          )}
        </div>
        <div className="tag-wrap">
          {f.values.map((v) => (
            <Pill key={v.id} label={v.label} dim={!v.current} maxLength={VALUE_NAME_MAX} draggable dragTitle="Drag into another category to move it"
              onDragStart={(e) => { setDrag({ kind: 'value', id: v.id, from: f.id }); e.dataTransfer.effectAllowed = 'move'; }}
              onDragEnd={endDrag}
              onRename={(label) => renameValue.mutate({ valueId: v.id, label })}
              onRemove={() => deleteValue.mutate({ valueId: v.id })}
              removeWarning={v.uses > 0 ? `${usedTimes(v.uses)}; those tags go too` : null}
              extra={
                <button type="button" className="tg-act" title={v.current ? 'Mark as old: kept for search, no longer offered' : 'Mark as current'} aria-label={v.current ? `Mark ${v.label} as old` : `Mark ${v.label} as current`}
                  onClick={() => upsertValue.mutate({ facetId: f.id, valueId: v.id, label: v.label, order: v.order, current: !v.current })}>
                  <Icon name={v.current ? 'clock' : 'undo'} />
                </button>
              } />
          ))}
          <PillInput placeholder="add value…" label={`Add ${f.label} value`} maxLength={VALUE_NAME_MAX}
            onAdd={(label) => upsertValue.mutate({ facetId: f.id, label, order: f.values.length })} />
        </div>
      </div>
    ),
  }));

  return (
    <div className="card pad facet-editor">
      <div className="settings-head">
        {media === 'audio'
          ? <div className="h2" title="Every sound effect in the library is filed along these, for every brand: the audio table's columns. Sounds are filed automatically from their names; a person's tags win.">Sound categories</div>
          : <div className="h2" title="Tags and search share these categories. The arrows or the handle reorder them; drag a value into another category to move it.">Tagging categories</div>}
        <div className="row facet-new-row">
          {adding && (
            <input className="tg-add facet-new" placeholder="New category, e.g. Occasion" aria-label="New tagging category name" maxLength={CATEGORY_NAME_MAX} autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') setAdding(false);
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const label = e.currentTarget.value.trim();
                if (label) addFacet(label);
              }}
              onBlur={(e) => { if (!e.currentTarget.value.trim()) setAdding(false); }} />
          )}
          <button type="button" className="icon-btn" title="New tagging category" aria-label="New tagging category" disabled={upsertFacet.isPending} onClick={() => setAdding(true)}>
            <Icon name="plus" />
          </button>
        </div>
      </div>
      <div className="facet-list">
        {cards.map((c) => <Fragment key={c.id}>{c.node}</Fragment>)}
      </div>
      {errors && <div className="error" role="alert">{errors.error?.message}</div>}
    </div>
  );
}
